import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  UserPlus, Users, Buildings, Copy, ArrowLeft, CheckCircle, PencilSimple,
} from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  companyScope, companyListUsers, companyInviteUser, companyRevokeUser,
  companyUpdateValidity,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function CompanyAdminPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const qc = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [editingValidity, setEditingValidity] = useState(null);

  const scope = useQuery({ queryKey: ["co-scope"], queryFn: () => companyScope() });
  const usersQ = useQuery({ queryKey: ["co-users"], queryFn: () => companyListUsers() });
  const revoke = useMutation({
    mutationFn: (uid) => companyRevokeUser(uid),
    onSuccess: () => {
      toast.success("User revoked");
      qc.invalidateQueries({ queryKey: ["co-users"] });
      qc.invalidateQueries({ queryKey: ["co-scope"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revoke failed"),
  });

  const s = scope.data || {};
  const users = usersQ.data || [];
  const approved = users.filter((u) => u.status === "approved");
  const revoked = users.filter((u) => u.status !== "approved");

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="company-admin-page">
      <Link to="/app" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-500 hover:text-brand-blue transition-colors" data-testid="co-back">
        <ArrowLeft size={12} weight="bold" /> Workspace
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-blue flex items-center gap-2">
            <Buildings size={12} weight="bold" /> Company admin panel
          </div>
          <h1 className="font-heading font-black text-4xl mt-1">{s.company || "—"}</h1>
          <div className="text-xs text-slate-500 mt-2">
            {s.approved_users ?? 0} active · {s.total_users ?? 0} total accounts
            {isSuper && <span className="ml-2 text-brand-blue/80">(acting as super-admin)</span>}
          </div>
        </div>
        <Button
          onClick={() => setInviting(true)}
          className="bg-brand-blue hover:bg-brand-blue-hover text-white rounded-sm"
          data-testid="co-invite-btn"
        >
          <UserPlus size={14} weight="bold" className="mr-2" /> Invite user
        </Button>
      </div>

      <div className="surface overflow-hidden" data-testid="co-users-list">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-widest">Email</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Role</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Validity</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...approved, ...revoked].map((u) => (
              <TableRow key={u.id} className="border-slate-200/40" data-testid={`co-row-${u.id}`}>
                <TableCell className="mono text-xs">{u.email}</TableCell>
                <TableCell className="text-xs">{u.name}</TableCell>
                <TableCell>
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${
                    u.role === "admin" ? "border-amber-500/50 text-amber-400 bg-amber-500/10"
                    : "border-slate-200 text-slate-500"
                  }`}>{u.role}</span>
                </TableCell>
                <TableCell>
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${
                    u.status === "approved" ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                    : "border-red-500/40 text-red-400 bg-red-500/10"
                  }`}>{u.status}</span>
                </TableCell>
                <TableCell className="mono text-[10px]">
                  {u.valid_from || u.valid_to ? `${u.valid_from || "—"} → ${u.valid_to || "∞"}` : "—"}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {u.status === "approved" && u.role !== "superadmin" && (
                    <>
                      <Button size="sm" variant="outline"
                        onClick={() => setEditingValidity(u)}
                        className="rounded-sm h-7 border-slate-200"
                        data-testid={`co-edit-validity-${u.id}`}>
                        <PencilSimple size={12} className="mr-1" /> Validity
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => confirm(`Revoke ${u.email}?`) && revoke.mutate(u.id)}
                        className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm h-7"
                        data-testid={`co-revoke-${u.id}`}>
                        Revoke
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!users.length && !usersQ.isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500 mono text-xs">
                  No users in {s.company || "your company"} yet. Invite one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
      {editingValidity && (
        <ValidityDialog user={editingValidity} onClose={() => setEditingValidity(null)} />
      )}
    </div>
  );
}


function ValidityDialog({ user, onClose }) {
  const qc = useQueryClient();
  const [validFrom, setValidFrom] = useState(user.valid_from || new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo]     = useState(user.valid_to   || new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10));
  const extendMonths = (months) => {
    const base = validTo ? new Date(validTo) : new Date();
    if (Number.isNaN(base.getTime())) return;
    base.setMonth(base.getMonth() + months);
    setValidTo(base.toISOString().slice(0, 10));
    toast.info(`+${months} months applied`);
  };
  const mut = useMutation({
    mutationFn: () => companyUpdateValidity(user.id, { valid_from: validFrom, valid_to: validTo }),
    onSuccess: () => {
      toast.success(`Validity updated for ${user.email}`);
      qc.invalidateQueries({ queryKey: ["co-users"] });
      qc.invalidateQueries({ queryKey: ["co-scope"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const canSubmit = validFrom && validTo && !mut.isPending;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-slate-200 max-w-md" data-testid="co-validity-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-widest text-sm">
            Update validity · {user.email}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Extend or shorten this user&apos;s access window. Both dates are required.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Valid from</Label>
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
              className="mono bg-white border-slate-200 h-10" data-testid="co-validity-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Valid to</Label>
            <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)}
              className="mono bg-white border-slate-200 h-10" data-testid="co-validity-to" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Quick extend</span>
          {[3, 6, 12, 24].map((m) => (
            <button key={m} type="button" onClick={() => extendMonths(m)}
              className="h-7 px-2 rounded-sm border border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 hover:text-brand-blue hover:border-brand-blue/50 transition-colors mono"
              data-testid={`co-validity-extend-${m}m`}>
              +{m}m
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!canSubmit}
            className="bg-brand-blue hover:bg-brand-blue-hover text-white rounded-sm"
            data-testid="co-validity-submit">
            {mut.isPending ? "Saving…" : "Save validity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InviteDialog({ onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: "", name: "",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to:   new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10),
  });
  const [created, setCreated] = useState(null);
  const mut = useMutation({
    mutationFn: () => companyInviteUser(form),
    onSuccess: (data) => {
      setCreated(data);
      toast.success(`Invited ${data.email}`);
      qc.invalidateQueries({ queryKey: ["co-users"] });
      qc.invalidateQueries({ queryKey: ["co-scope"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Invite failed"),
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const emailOk = /.+@.+\..+/.test(form.email);
  const canSubmit = emailOk && form.name.trim() && form.valid_from && form.valid_to && !mut.isPending;

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Copied"); }
    catch { toast.error("Clipboard denied"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-slate-200 max-w-lg" data-testid="co-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Invite team member</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Creates a company user with a temporary password. They&apos;ll be forced to change it on first login.
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-3">
            <div className="border border-brand-blue/50 bg-brand-blue/10 rounded-sm p-4 space-y-2">
              <div className="text-brand-blue text-[10px] uppercase tracking-widest flex items-center gap-1">
                <CheckCircle size={12} weight="fill" /> Invited
              </div>
              <div className="mono text-sm">{created.email}</div>
              <div className="flex items-center gap-2">
                <Input value={created.temp_password} readOnly
                  className="bg-white border-slate-200 mono text-xs"
                  onFocus={(e) => e.target.select()}
                  data-testid="co-invite-temp-pw" />
                <Button size="sm" variant="outline" className="rounded-sm border-slate-200"
                  onClick={() => copy(created.temp_password)}>
                  <Copy size={12} weight="bold" />
                </Button>
              </div>
              <div className="text-[10px] text-slate-500">
                Share this password securely.
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="bg-brand-blue hover:bg-brand-blue-hover text-white rounded-sm">
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="colleague@company.com" className="bg-white border-slate-200 mono" data-testid="co-invite-email" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Full name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                  className="bg-white border-slate-200" data-testid="co-invite-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">Valid from</Label>
                  <Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)}
                    className="mono bg-white border-slate-200 h-10" data-testid="co-invite-vf" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">Valid to</Label>
                  <Input type="date" value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)}
                    className="mono bg-white border-slate-200 h-10" data-testid="co-invite-vt" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={() => mut.mutate()} disabled={!canSubmit}
                className="bg-brand-blue hover:bg-brand-blue-hover text-white rounded-sm"
                data-testid="co-invite-submit">
                <Users size={14} weight="bold" className="mr-2" />
                {mut.isPending ? "Inviting…" : "Invite"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
